import React, { useState, useRef, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import logo from '../assets/photos/hlthIQ.png';
import '../assets/css/Research.css';

const ResearchSidebar = ({ isOpen, isCollapsed, toggleSidebar, isMobile, userRole }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const sidebarRef = useRef(null);
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const { logout } = useAuth();

  // Handle click outside to close sidebar on mobile
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobile && isOpen && sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        toggleSidebar();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen, toggleSidebar, isMobile]);

  // Handle escape key to close sidebar
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && isMobile && isOpen) {
        toggleSidebar();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, [isOpen, toggleSidebar, isMobile]);

  // Toggle submenu function
  const toggleSubmenu = (index) => {
    if (isCollapsed && !isMobile) {
      return; // Don't toggle submenu when collapsed on desktop
    }
    setActiveSubmenu(activeSubmenu === index ? null : index);
  };

  const getMenuItems = () => {
    switch (userRole) {
      case 'doctor':
        return [
          {
            icon: 'fas fa-tachometer-alt',
            label: 'Dashboard',
            path: '/doctor/dashboard'
          },
          {
            icon: 'fas fa-folder-open',
            label: 'My Batches',
            path: '/doctor/batches',
            submenu: [
              { label: 'View My Batches', path: '/doctor/batches' },
              // { label: 'My Cases', path: '/doctor/cases' }
            ]
          },
          {
            icon: 'fas fa-question-circle',
            label: 'Review Cases',
            path: '/doctor/review-cases'
          }
        ];
      
      case 'medical_admin':
        return [
          {
            icon: 'fas fa-tachometer-alt',
            label: 'Dashboard',
            path: '/medical-admin/dashboard'
          },
          {
            icon: 'fas fa-clipboard-list',
            label: 'View Protocols',
            path: '/medical-admin/protocols'
          },
          {
            icon: 'fas fa-folder-open',
            label: 'Batch Management',
            path: '/medical-admin/batch',
            submenu: [
              { label: 'Create & Manage Batches', path: '/medical-admin/batch' },
              { label: 'Assign Protocols to Batches', path: '/medical-admin/batch/assign-protocols' },
              { label: 'Assign Cases to Batches', path: '/medical-admin/batch/assign-cases' },
              { label: 'Assign Batches to Doctors', path: '/medical-admin/batch/assign-doctors' }
            ]
          },
          {
            icon: 'fas fa-chart-bar',
            label: 'View Reports',
            path: '/medical-admin/reports',
            submenu: [
              { label: 'All Reports', path: '/medical-admin/reports' },
              { label: 'Time Analytics', path: '/medical-admin/time-analytics' },
              { label: 'Quality of Answers', path: '/medical-admin/quality-of-answers' },
              { label: 'Data QA Interface', path: '/medical-admin/qa-data-quality' }
            ]
          },
          {
            icon: 'fas fa-user-md',
            label: 'Legacy Case Assignment',
            path: '/medical-admin/assign-cases'
          }
        ];
      
      case 'program_admin':
        return [
          {
            icon: 'fas fa-tachometer-alt',
            label: 'Dashboard',
            path: '/program-admin/dashboard'
          },
          {
            icon: 'fas fa-clipboard-list',
            label: 'Protocols',
            path: '/program-admin/protocols',
            submenu: [
              { 
                label: 'Create Protocol', 
                path: '/program-admin/create-protocol'  // Fix this path - don't make it a subpath
              },
              { 
                label: 'View All Protocols', 
                path: '/program-admin/protocols'
              }
            ]
          },
          {
            icon: 'fas fa-question-circle',
            label: 'Manage Questions',
            path: '/program-admin/questions',
            submenu: [
              { label: 'Create Questions', path: '/program-admin/questions' },
              { label: 'Edit Questions', path: '/program-admin/questions/view-all' },  // Updated path
              { label: 'View Answers', path: '/program-admin/view-answers', icon: 'fas fa-comment-dots' }
            ]
          },
          {
            icon: 'fas fa-link',
            label: 'Link Questions',
            path: '/program-admin/link-questions'
          },
          {
            icon: 'fas fa-user-md',
            label: 'Manage Doctors',
            path: '/program-admin/add-doctors'
          },
          {
            icon: 'fas fa-users',
            label: 'View All Cases',
            path: '/program-admin/cases'
          },
          {
            icon: 'fas fa-chart-bar',
            label: 'View Reports',
            path: '/program-admin/reports',
            submenu: [
              { label: 'All Reports', path: '/program-admin/reports' },
              { label: 'Time Analytics', path: '/program-admin/time-analytics' },
              { label: 'Statistical Reports', path: '/program-admin/statistical-reports' },
              { label: 'Quality of Answers', path: '/program-admin/quality-of-answers' },
              { label: 'Data QA Interface', path: '/program-admin/qa-data-quality' }
            ]
          },
          // {
          //   section: 'Research Management',
          //   items: [
          //     {
          //       icon: 'fas fa-clipboard-list',
          //       label: 'Protocols',
          //       path: '/program-admin/protocols',
          //       submenu: [
          //         { 
          //           label: 'View All Protocols', 
          //           path: '/program-admin/protocols',
          //           icon: 'fas fa-list'
          //         },
          //         { 
          //           label: 'Create Protocol', 
          //           path: '/program-admin/create-protocol',
          //           icon: 'fas fa-plus'
          //         },
          //         { 
          //           label: 'Edit Protocols', 
          //           path: '/program-admin/protocols', // Change this line
          //           icon: 'fas fa-edit'
          //         }
          //       ]
          //     }
          //   ]
          // }
        ];
      
      case 'statistician':
        return [
          {
            icon: 'fas fa-tachometer-alt',
            label: 'Dashboard',
            path: '/statistician/dashboard'
          },
          {
            icon: 'fas fa-chart-line',
            label: 'Reports',
            path: '/statistician/reports',
            submenu: [
              { label: 'Create Reports', path: '/statistician/reports' },
              { label: 'View Reports', path: '/statistician/reports/view' },
              { label: 'Time Analytics', path: '/medical-admin/time-analytics' }
            ]
          },
          {
            icon: 'fas fa-database',
            label: 'Research Data',
            path: '/statistician/data'
          },
          {
            icon: 'fas fa-chart-bar',
            label: 'Analytics',
            path: '/statistician/analytics'
          },
          {
            icon: 'fas fa-cog',
            label: 'Settings',
            path: '/settings'
          }
        ];
      
      default:
        return [
          {
            icon: 'fas fa-tachometer-alt',
            label: 'Dashboard',
            path: '/dashboard'
          },
          {
            icon: 'fas fa-clipboard-list',
            label: 'Protocols',
            path: '/protocols'
          },
          {
            icon: 'fas fa-users',
            label: 'Cases',
            path: '/cases'
          },
          {
            icon: 'fas fa-chart-bar',
            label: 'Reports',
            path: '/reports'
          },
          {
            icon: 'fas fa-cog',
            label: 'Settings',
            path: '/settings'
          }
        ];
    }
  };

  // Handle logout
  const handleLogout = () => {
    logout();
    // Redirect to login page or handle in your auth context
  };
  
  // Format role for display
  const formatRole = (role) => {
    if (!role) return 'User';
    return role
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };
  
  // Update this function to check for exact path matches for submenu items
  const isPathActive = (path) => {
    if (path.includes('/program-admin/protocols/create-protocol')) {
      // For create protocol, check exact match
      return location.pathname === path;
    } else if (path === '/program-admin/protocols') {
      // For the protocols list page, check it's not a subpage
      return location.pathname === path;
    }
    
    // For other pages, keep the current behavior
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };
  
  const menuItems = getMenuItems();
  
  // Get sidebar class based on states
  const getSidebarClass = () => {
    let className = 'research-sidebar';
    if (isMobile) {
      if (isOpen) className += ' open';
      else className += ' closed';
    } else {
      if (isCollapsed) className += ' collapsed';
    }
    return className;
  };

  return (
    <>
      {/* Sidebar overlay for mobile */}
      {isMobile && isOpen && (
        <div className="research-sidebar-backdrop" onClick={toggleSidebar} />
      )}

      {/* Main sidebar component */}
      <aside 
        ref={sidebarRef}
        className={getSidebarClass()}
        aria-hidden={isMobile && !isOpen}
      >
        {/* Mobile close button */}
        {isMobile && isOpen && (
          <button 
            className="sidebar-close-btn" 
            onClick={toggleSidebar} 
            aria-label="Close sidebar"
          >
            <i className="fas fa-times"></i>
          </button>
        )}

        {/* Sidebar header with logo */}
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <img src={logo} alt="HealthIQ Logo" />
          </div>
          <div className="sidebar-role">
            <span className="role-badge">{formatRole(userRole)}</span>
          </div>
        </div>

        {/* Navigation */}
        <nav className="sidebar-nav">
          <ul className="sidebar-menu">
            {menuItems.map((item, index) => (
              <li key={index} className={`sidebar-menu-item ${item.submenu ? 'has-submenu' : ''}`}>
                {item.submenu ? (
                  // Menu item with submenu
                  <>
                    <button
                      className={`sidebar-menu-link ${isPathActive(item.path) ? 'active' : ''} ${activeSubmenu === index ? 'submenu-open' : ''}`}
                      onClick={() => toggleSubmenu(index)}
                      aria-expanded={activeSubmenu === index}
                    >
                      <span className="sidebar-menu-icon">
                        <i className={item.icon}></i>
                      </span>
                      <span className="sidebar-menu-text">{item.label}</span>
                      <span className="sidebar-menu-arrow">
                        <i className={`fas fa-chevron-${activeSubmenu === index ? 'up' : 'down'}`}></i>
                      </span>
                    </button>
                    <div className={`sidebar-submenu ${activeSubmenu === index ? 'open' : ''}`}>
                      <ul>
                        {item.submenu.map((subItem, subIndex) => (
                          <li key={subIndex} className="sidebar-submenu-item">
                            <NavLink
                              to={subItem.path}
                              className={({ isActive }) => 
                                `sidebar-submenu-link ${
                                  isActive || 
                                  (location.pathname === subItem.path) ? 'active' : ''
                                }`
                              }
                              onClick={isMobile ? toggleSidebar : undefined}
                            >
                              <span>{subItem.label}</span>
                            </NavLink>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </>
                ) : (
                  // Regular menu item
                  <NavLink
                    to={item.path}
                    className={({ isActive }) => 
                      `sidebar-menu-link ${isActive ? 'active' : ''}`
                    }
                    onClick={isMobile ? toggleSidebar : undefined}
                    title={isCollapsed && !isMobile ? item.label : undefined}
                  >
                    <span className="sidebar-menu-icon">
                      <i className={item.icon}></i>
                    </span>
                    <span className="sidebar-menu-text">{item.label}</span>
                    {/* Badge for new items */}
                    {item.badge && (
                      <span className="sidebar-menu-badge">{item.badge}</span>
                    )}
                  </NavLink>
                )}
              </li>
            ))}
          </ul>
        </nav>

        {/* Sidebar footer */}
        <div className="sidebar-footer">
          <div className="sidebar-footer-actions">
            <button 
              className="sidebar-footer-btn" 
              onClick={() => {
                navigate('/help');
                if (isMobile) toggleSidebar();
              }}
              title={isCollapsed && !isMobile ? "Help" : undefined}
            >
              <i className="fas fa-question-circle"></i>
              <span>Help</span>
            </button>
            <button 
              className="sidebar-footer-btn" 
              onClick={handleLogout}
              title={isCollapsed && !isMobile ? "Logout" : undefined}
            >
              <i className="fas fa-sign-out-alt"></i>
              <span>Logout</span>
            </button>
          </div>
          <div className="sidebar-footer-brand">
            <span>Health.IQ</span>
            <small>Research Platform</small>
          </div>
        </div>
      </aside>
    </>
  );
};

export default ResearchSidebar;